import { Select as DSSelect, Field, type FieldClassNames } from "@eisox/design-system";

import { useFieldErrors } from "../field.hook";
import { useFieldContext } from "../form.context";
import { FieldErrors } from "./field-errors.component";

interface SelectClassNames extends FieldClassNames {
  content?: string;
  viewport?: string;
  item?: string;
  value?: string;
}

interface SelectOption {
  value: string;
  name: string;
  disabled?: boolean;
}

interface SelectOptionGroup {
  heading: string;
  options: SelectOption[];
}

const SEARCH_INPUT_THRESHOLD = 10;

interface SelectProps extends Omit<React.ComponentProps<typeof DSSelect.Root<boolean>>, "children" | "classNames"> {
  options: SelectOption[];
  groups?: SelectOptionGroup[];
  label?: string;
  description?: string;
  placeholder?: string;
  side?: DSSelect.ContentProps["side"];
  searchInput?: boolean;
  disabled?: boolean;
  emptyLabel?: string;
  classNames?: SelectClassNames;
}

const Select: React.FC<SelectProps> = ({
  options,
  groups = [],
  label,
  description,
  placeholder,
  side,
  searchInput = true,
  disabled,
  emptyLabel,
  classNames,
  onValueChange,
  ...props
}) => {
  const field = useFieldContext<string | string[] | undefined>();
  const errors = useFieldErrors();

  const optionCount = options.length + groups.flatMap(group => group.options).length;
  const renderOption = (option: SelectOption) => (
    <DSSelect.Item className={classNames?.item} disabled={option.disabled} key={option.value} value={option.value}>
      {option.name}
    </DSSelect.Item>
  );

  return (
    <Field.Root className={classNames?.root}>
      <Field.Label className={classNames?.label}>{label}</Field.Label>
      <Field.Description className={classNames?.description}>{description}</Field.Description>
      <Field.Control
        render={
          <DSSelect.Root
            {...props}
            value={field.state.value}
            onValueChange={value => {
              field.handleChange(value);
              onValueChange?.(value);
            }}
          >
            <DSSelect.Trigger
              className={classNames?.control}
              error={errors.length > 0}
              onBlur={() => {
                field.handleBlur();
              }}
              disabled={disabled}
            >
              <DSSelect.Value className={classNames?.value} placeholder={placeholder} />
            </DSSelect.Trigger>
            <DSSelect.Content className={classNames?.content} side={side}>
              {optionCount > SEARCH_INPUT_THRESHOLD && searchInput && <DSSelect.Input />}
              <DSSelect.Viewport className={classNames?.viewport}>
                {options.map(renderOption)}
                {groups.map(group => (
                  <DSSelect.Group key={group.heading} heading={group.heading}>
                    {group.options.map(renderOption)}
                  </DSSelect.Group>
                ))}
                {emptyLabel && <DSSelect.Empty>{emptyLabel}</DSSelect.Empty>}
              </DSSelect.Viewport>
            </DSSelect.Content>
          </DSSelect.Root>
        }
      />
      <FieldErrors className={classNames?.error} />
    </Field.Root>
  );
};

export { Select };
export type { SelectClassNames, SelectOption, SelectOptionGroup, SelectProps };
